import React, { useState, HTMLProps } from "react";

import NumericKeyboard from "src/components/numericKeyboard";
import { decimalString } from "src/constants/types";
import { useConfig } from "src/hooks/config";
import { useTranslation } from "src/hooks/translation";

interface IProps extends Omit<HTMLProps<HTMLInputElement>, "onChange"> {
	value?: decimalString;
	customButtons?: decimalString[];
	onChange(value: decimalString): void;
	maxDecimalCount?: number;
	maxLength?: number;
	placeholder?: string;
	toggleKeyboard?: () => void;
	isMobile?: boolean;
}

const NumericInput = ({
	toggleKeyboard,
	value = "",
	customButtons,
	onChange,
	maxLength = Number.MAX_SAFE_INTEGER,
	maxDecimalCount,
	placeholder = "",
	className,
	isMobile,
	...rest
}: IProps) => {
	const config = useConfig();
	const [keyboardOpen, setKeyboardOpen] = useState(false);

	// const listenToEvents = !isTerminal();

	const onRemove = () => {
		if (!value) return;
		const valueStr = value.toString();
		onChange(valueStr.substring(0, valueStr.length - 1) as decimalString);
	};

	const onKeyboardButtonClick = (sign: decimalString, isCustomButton: boolean) => {
		let newValue = "";

		if (isCustomButton) newValue = config.enableAdditiveLogic ? `${+value + +sign}` : `${sign}`;
		else newValue = `${value}${sign}`;

		validationPattern.test(newValue) && newValue.length <= maxLength && onChange(newValue as decimalString);
	};

	const onOpenKeyboard = () => {
		toggleKeyboard?.();
		setKeyboardOpen(!keyboardOpen);
	};

	const onCloseKeyboard = () => {
		toggleKeyboard?.();
		setKeyboardOpen(false);
	};

	const t = useTranslation();

	const validationPattern = maxDecimalCount
		? new RegExp(`^([0-9]*)([\.]?)([0-9]{0,${maxDecimalCount}})$`)
		: new RegExp(`^([0-9]*)([\.]?)([0-9]*)$`);

	const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		// const newValue = e.currentTarget.value;
		const newValue = e.currentTarget.value.replace(/,/g, '.');

		if (!validationPattern.test(newValue) || newValue.length > maxLength) return null;

		onChange(newValue as decimalString);
	};

	return (
		<>
			<input
				{...rest}
				value={value}
				placeholder={t(placeholder)}
				className={className}
				onClick={onOpenKeyboard}
				onChange={onInputChange}
				inputMode="decimal"
				pattern="[0-9]*[.,]?[0-9]*"
			/>

			{(keyboardOpen && !isMobile) && (
				<NumericKeyboard
					customButtons={customButtons}
					onClick={onKeyboardButtonClick}
					outsideException={`.${className}`}
					onRemove={onRemove}
					onClose={onCloseKeyboard}
					isMobile={isMobile}
				/>
			)}
		</>
	);
};

export default NumericInput;
